import React, { useState, useEffect } from 'react';
import './ActivitiesPage.css';

const ACTIVITIES_DATA = [
  {
    id: 1,
    title: 'Read for 20 Minutes',
    description: 'Pick up a book or article and engage in deep reading',
    icon: '📚',
    color: '#3b82f6',
    duration: 20,
    category: 'Mental',
    points: 20,
  },
  {
    id: 2,
    title: 'Meditation Session',
    description: 'Practice mindfulness and calm your racing thoughts',
    icon: '🧘',
    color: '#8b5cf6',
    duration: 10,
    category: 'Mental',
    points: 15,
  },
  {
    id: 3,
    title: 'Physical Exercise',
    description: 'Get moving with a workout, walk, or stretching routine',
    icon: '🏃',
    color: '#10b981',
    duration: 30,
    category: 'Physical',
    points: 30,
  },
  {
    id: 4,
    title: 'Creative Writing',
    description: 'Journal your thoughts or write creatively',
    icon: '✍️',
    color: '#f59e0b',
    duration: 15,
    category: 'Creative',
    points: 15,
  },
  {
    id: 5,
    title: 'Learn Something New',
    description: 'Take an online course or watch an educational video',
    icon: '🎓',
    color: '#ec4899',
    duration: 25,
    category: 'Mental',
    points: 25,
  },
  {
    id: 6,
    title: 'Social Connection',
    description: 'Call a friend or family member for a real conversation',
    icon: '📞',
    color: '#06b6d4',
    duration: 15,
    category: 'Social',
    points: 20,
  },
  {
    id: 7,
    title: 'Skill Practice',
    description: 'Practice a musical instrument, drawing, or any skill',
    icon: '🎸',
    color: '#f97316',
    duration: 30,
    category: 'Creative',
    points: 30,
  },
  {
    id: 8,
    title: 'Nature Walk',
    description: 'Spend time outdoors and disconnect from screens',
    icon: '🌳',
    color: '#22c55e',
    duration: 20,
    category: 'Physical',
    points: 20,
  },
  {
    id: 9,
    title: 'Cooking/Baking',
    description: 'Prepare a meal from scratch with full attention',
    icon: '👨‍🍳',
    color: '#ef4444',
    duration: 40,
    category: 'Creative',
    points: 35,
  },
  {
    id: 10,
    title: 'Digital Detox Hour',
    description: 'Complete one hour without any digital devices',
    icon: '📵',
    color: '#6366f1',
    duration: 60,
    category: 'Mental',
    points: 50,
  },
];

const ActivitiesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [completedActivities, setCompletedActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const categories = ['All', 'Mental', 'Physical', 'Creative', 'Social'];

  useEffect(() => {
    loadCompletedActivities();
  }, []);

  const loadCompletedActivities = () => {
    const data = localStorage.getItem('completedActivities');
    if (data) {
      setCompletedActivities(JSON.parse(data));
    }
  };

  const isActivityCompleted = (activityId) => {
    const today = new Date().toDateString();
    return completedActivities.some(
      (item) => item.id === activityId && item.date === today
    );
  };

  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setShowModal(true);
  };

  const handleCompleteActivity = () => {
    if (!selectedActivity) return;

    const today = new Date().toDateString();
    const newCompletion = {
      id: selectedActivity.id,
      date: today,
      timestamp: new Date().toISOString(),
    };

    const updated = [...completedActivities, newCompletion];
    localStorage.setItem('completedActivities', JSON.stringify(updated));
    setCompletedActivities(updated);

    // Update stats
    const statsData = localStorage.getItem('userStats');
    const stats = statsData
      ? JSON.parse(statsData)
      : {
          streak: 0,
          activitiesCompleted: 0,
          totalMinutesSaved: 0,
          weekProgress: [0, 0, 0, 0, 0, 0, 0],
        };

    stats.activitiesCompleted += 1;
    stats.totalMinutesSaved += selectedActivity.duration;

    const dayIndex = new Date().getDay();
    const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1;
    stats.weekProgress[adjustedIndex] += 1;

    localStorage.setItem('userStats', JSON.stringify(stats));

    alert(
      `🎉 Awesome! You earned ${selectedActivity.points} points and reclaimed ${selectedActivity.duration} minutes from brain rot!`
    );
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedActivity(null);
  };

  const filteredActivities = ACTIVITIES_DATA.filter(
    (activity) =>
      selectedCategory === 'All' || activity.category === selectedCategory
  );

  return (
    <div className="activities-page">
      <div className="container">
        <div className="page-header">
          <h1>Activities Library</h1>
          <p>Choose activities to replace mindless scrolling</p>
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${
                selectedCategory === category ? 'active' : ''
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Activities Grid */}
        <div className="activities-grid">
          {filteredActivities.map((activity) => {
            const completed = isActivityCompleted(activity.id);

            return (
              <div
                key={activity.id}
                className={`activity-card ${completed ? 'completed' : ''}`}
                onClick={() => handleActivityClick(activity)}
              >
                {completed && (
                  <div className="completed-badge">
                    <span>✓</span>
                  </div>
                )}

                <div
                  className="activity-icon"
                  style={{ backgroundColor: activity.color }}
                >
                  <span>{activity.icon}</span>
                </div>

                <h3 className="activity-title">{activity.title}</h3>
                <p className="activity-description">{activity.description}</p>

                <div className="activity-meta">
                  <span className="meta-item">
                    <span>⏱️</span> {activity.duration} min
                  </span>
                  <span className="meta-item">
                    <span>⭐</span> {activity.points} pts
                  </span>
                </div>

                <div className="category-badge" style={{ backgroundColor: activity.color }}>
                  {activity.category}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedActivity && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ✕
            </button>

            <div
              className="modal-icon"
              style={{ backgroundColor: selectedActivity.color }}
            >
              <span>{selectedActivity.icon}</span>
            </div>

            <h2 className="modal-title">{selectedActivity.title}</h2>
            <p className="modal-description">{selectedActivity.description}</p>

            <div className="modal-stats">
              <div className="modal-stat">
                <span className="stat-icon">⏱️</span>
                <span className="stat-value">{selectedActivity.duration} min</span>
                <span className="stat-label">Duration</span>
              </div>

              <div className="modal-stat">
                <span className="stat-icon">⭐</span>
                <span className="stat-value">{selectedActivity.points} pts</span>
                <span className="stat-label">Points</span>
              </div>
            </div>

            {isActivityCompleted(selectedActivity.id) ? (
              <div className="completed-message">
                <span className="check-icon">✓</span>
                <span>Completed today! 🎉</span>
              </div>
            ) : (
              <button className="btn btn-primary complete-btn" onClick={handleCompleteActivity}>
                Mark as Complete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitiesPage;